/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Pipeline Page State Controller Hook
 *
 * Centralized state management for the sales pipeline page including:
 * - Data fetching for pipeline opportunities
 * - Filter state management
 * - Pagination and sorting
 * - Saved views
 * - View mode (board/summary)
 */

"use client";

import { usePipelineMetrics } from "@/components/features/clients/pipeline-metrics";
import type { FilterOption } from "@/components/shared/standard-filters";
import type { PipelineOpportunity } from "@/hooks/data/estimates";
import { usePipelineEstimates } from "@/hooks/data/estimates";
import { formatCurrencyNullable } from "@/lib/utils/formatters";
import {
  CLIENT_STAGE_CONFIG,
  type ClientStage,
  type ClientStatus,
} from "@/types/client-unified";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import type {} from "react";
import { useDomainSearch } from "../data/use-domain-search";
import { useSavedViewsUnified } from "../saved-views/use-saved-views-unified";
import { useStandardFilters } from "./use-standard-filters";

type PipelineSavedViewFilters = {
  search: string;
  status: ClientStatus | "all";
  stage: ClientStage | "all";
  priority: "all" | "high" | "urgent";
  view: "board" | "summary";
};

const SAVED_VIEWS_STORAGE_KEY = "sales-pipeline-views";

export function usePipelinePageState() {
  const router = useNavigationWithProgress();

  // Standard filters (search, pagination, base filters)
  const {
    searchTermInput,
    setSearchTermInput,
    searchTermServer,
    page,
    setPage,
    pageSize,
    setPageSize,
    filterState,
    setFilterState,
    handleClearAllFilters,
    handleClearSearch,
  } = useStandardFilters({
    id: "pipeline",
    initialFilters: {
      status: "all",
      stage: "all",
      priority: "all",
      view: "board",
    },
    pageSize: 20,
    syncWithUrl: true,
  });

  // Filter state extraction
  const pipelineStatusFilter =
    (filterState.status as ClientStatus | "all") ?? "all";
  const pipelineStageFilter =
    (filterState.stage as ClientStage | "all") ?? "all";
  const pipelinePriorityFilter =
    (filterState.priority as "all" | "high" | "urgent") ?? "all";
  const pipelineActiveView =
    (filterState.view as "board" | "summary") ?? "board";

  // Data fetching - Pipeline
  const {
    opportunities: pipelineOpportunities,
    pagination: _pipelinePagination,
    isLoading: pipelineLoading,
    error: pipelineError,
  } = usePipelineEstimates({
    page,
    limit: pageSize,
    search: searchTermServer || undefined,
    stage: pipelineStageFilter as ClientStage | "all",
  });

  // Pipeline data processing
  const STAGE_ORDER: ClientStage[] = [
    "IN_PROCESS",
    "PENDING_REVIEW",
    "REVISION",
    "SUBMITTED",
    "APPROVED",
    "REJECTED",
  ];

  const pipelineFilteredOpportunities = !pipelineOpportunities
    ? ([] as PipelineOpportunity[])
    : pipelineOpportunities.filter((opportunity: PipelineOpportunity) => {
        const matchesSearch =
          !searchTermServer ||
          opportunity.name
            .toLowerCase()
            .includes(searchTermServer.toLowerCase()) ||
          opportunity.clientName
            .toLowerCase()
            .includes(searchTermServer.toLowerCase()) ||
          opportunity.clientCompany
            ?.toLowerCase()
            .includes(searchTermServer.toLowerCase());
        return matchesSearch;
      });

  const pipelineOpportunitiesByStage = (() => {
    const base = Object.keys(CLIENT_STAGE_CONFIG).reduce(
      (acc, key) => {
        acc[key as ClientStage] = [] as PipelineOpportunity[];
        return acc;
      },
      {} as Record<ClientStage, PipelineOpportunity[]>,
    );

    pipelineFilteredOpportunities.forEach((opportunity) => {
      const stage = (opportunity.stage as ClientStage) ?? "IN_PROCESS";
      base[stage] = base[stage] ?? [];
      base[stage].push(opportunity);
    });

    return base;
  })();

  const pipelineBoardColumns = (() => {
    const orderedStages = [
      ...STAGE_ORDER,
      ...Object.keys(CLIENT_STAGE_CONFIG).filter(
        (stage) => !STAGE_ORDER.includes(stage as ClientStage),
      ),
    ] as ClientStage[];

    return orderedStages.map((stage) => {
      const meta = CLIENT_STAGE_CONFIG[stage];
      return {
        stage,
        label: meta.label,
        description: meta.description,
        color: meta.color,
        opportunities: pipelineOpportunitiesByStage[stage] ?? [],
      };
    });
  })();

  const pipelineStageOptions: FilterOption[] = [
    { value: "all", label: "All Stages" },
    ...Object.entries(CLIENT_STAGE_CONFIG).map(([id, meta]) => ({
      value: id,
      label: meta.label,
    })),
  ];

  // Pipeline filters
  const handlePipelineStageChange = (value: ClientStage | "all") => {
    setFilterState({ stage: value });
    setPage(1);
  };

  const handlePipelineViewChange = (value: "board" | "summary") => {
    setFilterState({ view: value });
  };

  // Saved views - Pipeline
  const pipelineCurrentFilters = {
    search: searchTermServer || "",
    status: pipelineStatusFilter,
    stage: pipelineStageFilter,
    priority: pipelinePriorityFilter,
    view: pipelineActiveView,
  };

  const pipelineDefaultFilters = {
    search: "",
    status: "all" as ClientStatus | "all",
    stage: "all" as ClientStage | "all",
    priority: "all" as "all" | "high" | "urgent",
    view: "board" as "board" | "summary",
  };

  const {
    views: pipelineSavedViews,
    selectedViewId: pipelineSelectedViewId,
    isDirty: pipelineIsDirty,
    handleApplyView: handlePipelineApplyView,
    handleSaveView: handlePipelineSaveView,
    handleDeleteView: handlePipelineDeleteView,
  } = useSavedViewsUnified<PipelineSavedViewFilters>({
    viewType: "pipeline",
    storageKey: SAVED_VIEWS_STORAGE_KEY,
    currentFilters: pipelineCurrentFilters,
    defaultFilters: pipelineDefaultFilters,
    onApplyView: (filters: PipelineSavedViewFilters) => {
      setSearchTermInput(filters.search || "");
      setFilterState({
        status: filters.status || "all",
        stage: filters.stage || "all",
        priority: filters.priority || "all",
        view: filters.view || "board",
      });
    },
    syncWithUrl: true,
    enabled: true,
  });

  const pipelineHasActiveFilters =
    Boolean(searchTermServer) ||
    pipelineStatusFilter !== "all" ||
    pipelineStageFilter !== "all" ||
    pipelinePriorityFilter !== "all";

  // Metrics calculators
  const pipelineMetricsCalculators = usePipelineMetrics({
    isLoading: pipelineLoading,
    hasActiveFilters: pipelineHasActiveFilters,
    formatCurrency: (v: number | null) =>
      formatCurrencyNullable(v, "USD", "en-US", "$0"),
  });

  // Search hook
  const { searchInputRef, searchProps, focusSearch } = useDomainSearch({
    placeholder: "Search by client, company, or email",
    searchTerm: searchTermInput,
    onSearchChange: setSearchTermInput,
    onClear: handleClearSearch,
    canCreate: false,
    testIdPrefix: "pipeline",
  });

  return {
    // Search
    searchTermInput,
    setSearchTermInput,
    searchTermServer,
    searchInputRef,
    searchProps,
    focusSearch,

    // Pagination
    page,
    setPage,
    pageSize,
    setPageSize,

    // Filters
    pipelineStatusFilter,
    pipelineStageFilter,
    pipelinePriorityFilter,
    pipelineActiveView,
    pipelineStageOptions,
    handlePipelineStageChange,
    handlePipelineViewChange,
    handleClearAllFilters,
    handleClearSearch,
    pipelineHasActiveFilters,

    // Data
    pipelineOpportunities: pipelineFilteredOpportunities,
    pipelineBoardColumns,
    pipelineLoading,
    pipelineError:
      pipelineError instanceof Error
        ? pipelineError
        : pipelineError
          ? new Error(String(pipelineError))
          : null,

    // Metrics
    pipelineMetricsCalculators,

    // Saved views
    pipelineSavedViews,
    pipelineSelectedViewId: pipelineSelectedViewId ?? null,
    pipelineIsDirty,
    handlePipelineApplyView,
    handlePipelineSaveView,
    handlePipelineDeleteView,

    // Router
    router,
  };
}
